import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

const posterPriceQueue = [];
const posterPriceRequests = new Map();
let activePosterPriceRequests = 0;

function runPosterPriceQueue() {
  while (activePosterPriceRequests < 4 && posterPriceQueue.length) {
    const task = posterPriceQueue.shift();
    activePosterPriceRequests += 1;
    task().finally(() => {
      activePosterPriceRequests -= 1;
      runPosterPriceQueue();
    });
  }
}

function getPosterCardPrice(url) {
  const key = url.href;
  if (!posterPriceRequests.has(key)) {
    const request = new Promise((resolve, reject) => {
      posterPriceQueue.push(async () => {
        try {
          const html = await sectionRenderer.getSectionHTML('oh-poster-card-price', true, url);
          const document = new DOMParser().parseFromString(html, 'text/html');
          const price = document.querySelector('[data-oh-card-price]');
          if (!price) throw new Error('Poster card price unavailable');
          resolve(price);
        } catch (error) {
          posterPriceRequests.delete(key);
          reject(error);
        }
      });
    });
    posterPriceRequests.set(key, request);
    runPosterPriceQueue();
  }
  return posterPriceRequests.get(key);
}

class PosterCardPrice extends HTMLElement {
  connectedCallback() {
    if (!this.closest('product-card, .resource-card[data-resource-type="product"]')) return;
    this.updateStartingPrice();
  }

  async updateStartingPrice() {
    const productId = this.dataset.productId;
    const container = this.querySelector('[ref="priceContainer"]');
    if (!container || !this.dataset.productUrl) return;
    const url = new URL(this.dataset.productUrl, window.location.origin);
    if (url.origin !== window.location.origin) return;
    try {
      const price = await getPosterCardPrice(url);
      if (!this.isConnected || this.dataset.productId !== productId || price.dataset.productId !== productId) return;
      const currentContainer = this.querySelector('[ref="priceContainer"]');
      if (!currentContainer) return;
      currentContainer.replaceChildren(price.cloneNode(true));
      this.dataset.priceReady = 'true';
    } catch {
      this.dataset.priceReady = 'unavailable';
    }
  }
}

if (!customElements.get('oh-poster-card-price')) {
  customElements.define('oh-poster-card-price', PosterCardPrice);
}
